import { ModelSettings } from '../components/moleculas/inputform/types';

export const MAX_FILE_SIZE = 10000;

export const modelSettings: { [key: string]: ModelSettings } = {
  mii_lng: {
    allowedFileTypes: ['xls', 'xlsx'],
    sheets: [
      {
        name: 'Facility Summary',
        columns: [
          'Facility Name',
          'First Scan Date Time (UTC)',
          'Last Scan Date Time (UTC)',
          'Emission Sources Seen',
          'Total Facility Emission (SCFH)',
        ],
      },
      {
        name: 'Emission Location Summary',
        columns: [
          'Facility Name',
          'Emission Location',
          'First Scan Date Time (UTC)',
          'Last Scan Date Time (UTC)',
          'Average Plume Height (ft)',
          'Avg Emission Rate (SCFH)',
          'Persistence Rate',
          'Number Of Scans',
          'Latitude',
          'Longitude',
        ],
      },
      {
        name: 'Emission Location Extended',
        columns: [
          'Facility Name',
          'Emission Location',
          'Scan Date Time (UTC)',
          'Gas Detected',
          'Detection Id',
          'Emission Location Within Facility Polygon',
          'Emission Rate (SCFH)',
          'Emitter Height (ft)',
          'Max Concentration (ppm-m)',
          'Wind Speed (mph)',
          'Persistence',
          'Latitude',
          'Longitude',
        ],
      },
    ],
    title: 'Measurement Informed Inventory: LNG',
    fileField: 'file',
    isDownloadAllowed: false,
    description:
      '        This model is based on <a href="https://chemrxiv.org/engage/chemrxiv/article-details/66c67032a4e53c48762fb9e3">\n' +
      '          Multi-scale Measurements of Greenhouse Gas Emissions at U.S. Natural\n' +
      '          Gas Liquefaction Terminals\n' +
      '        </a>\n' +
      '        . The intention of this model is to enable operators of LNG supply chain\n' +
      '        components to better estimate their methane emissions based on survey\n' +
      '        data. The interface expects the Bridger survey template format. Please\n' +
      '        remove any rows that contain emission sources out of the LNG terminal\n' +
      '        site before uploading file.',
    paramsMapping: {
      param1: {
        name: 'turbine_exhaust',
        label: 'Turbine Exhaust',
        description: 'Turbine exhaust emissions (annualized), SCFH',
      },
      param2: {
        name: 'thermal_oxidizer',
        label: 'Thermal oxidizer',
        description: 'Thermal oxidizers emission (annualized), SCFH',
      },
      param3: {
        name: 'small_fugitives',
        label: 'Small Fugitives',
        description: 'Small fugitive emissions (annualized), SCFH',
      },
      param4: {
        name: 'aircraft_type',
        label: 'Aircraft Type',
        description: '',
        values: [
          { label: 'Fixed Wing', value: 'fixed_wing' },
          { label: 'Rotary Wing', value: 'rotary_wing' },
        ],
      },
      param5: {
        name: 'LNG_production_rate',
        label: 'LNG Production Rate',
        description: 'The production rate of LNG in MMcf/year',
      },
      param6: {
        name: 'methane_mol_con',
        label: 'Methane molar concentration',
        description:
          'The molar concentration of methane in the produced LNG (in percentage)',
      },
    },
    fileTemplateToDownload: 'download-template.xlsx',
    submitButtonLabel: 'Run MII Model',
    rowToValidate: 4,
    apiUrl: {
      DEV: 'https://flexplanfuncmodels.azurewebsites.net/api/liquefaction', //dev env azure
      // DEV: 'http://localhost:3000/FlexTest',   //node
      // DEV: 'http://localhost:7071/api/liquefaction', // local pyt
      QA: 'https://flexplanfuncmodelsqa.azurewebsites.net/api/liquefaction',
      PROD: 'https://flexplanfuncmodelsprod.azurewebsites.net/api/liquefaction',
    },
  },
  mii_upstream: {
    allowedFileTypes: ['xls', 'xlsx'],
    sheets: [
      {
        name: 'Anonymized Q3 Emission Extended',
        columns: [
          'Region',
          'Facility Name',
          'Facility Type Ami',
          'Sponsored',
          'Oil And Gas',
          'Emission Location Id',
          'Detection Id',
          'Flight',
          'Equipment Id',
          'Equipment Type',
          'Gas Detected',
          'Emission Rate (SCFH)',
          'Persistence',
          'Detection Condition',
          'Emitter In Buffer',
        ],
      },
      {
        name: 'Anonymized Q3 Equip Aggregate',
        columns: [
          'Region',
          'Facility Name',
          'Equipment Type',
          'Equipment Id',
          'Scan Count',
          'Persistence',
          'Avg Emission Rate (SCFH)',
        ],
      },
      {
        name: 'Anonymized Q3 Facility Summary',
        columns: [
          'Region',
          'Facility Name',
          'Facility Type Ami',
          'Sponsored',
          'Oil And Gas',
          'Emission Location Count',
          'Gas Detected',
        ],
      },
      {
        name: 'AMI Op Q3 Emission Extended',
        columns: [
          'Region',
          'AMI Operator',
          'Ami Facility Type',
          'Site Id',
          'Emission Location Id',
          'Detection Id',
          'Equipment Id',
          'Equipment Type',
          'Scan Date Time (UTC)',
          'Gas Detected',
          'Emission Rate (SCFH)',
          'Persistence',
        ],
      },
      {
        name: 'AMI Op Q3 Equip Aggregate',
        columns: [
          'Region',
          'AMI Operator',
          'Ami Facility Type',
          'Site Id',
          'Equipment Type',
          'Equipment Id',
          'Scan Count',
          'First Scan (UTC)',
          'Last Scan (UTC)',
          'Persistence',
          'Avg Emission Rate (SCFH)',
        ],
      },
      {
        name: 'AMI Op Q3 Facility Summary',
        columns: [
          'Region',
          'AMI Operator',
          'Ami Facility Type',
          'Site Id',
          'First Scan Date Time (UTC)',
          'Last Scan Date Time (UTC)',
          'Emission Location Count',
          'Gas Detected',
          'Intermittent Facility Emission (SCFH)',
          'Persistent Facility Emission (SCFH)',
          'Total Facility Emission (SCFH)',
        ],
      },
      {
        name: 'Pneumatic Controller EF',
        columns: [
          'Basin',
          'Pneumatic Controller Types',
          'Count Per Well',
          'Leaker EF (scf whole gas/hour/unit)',
        ],
      },
      {
        name: 'Pneumatic Pump EF',
        columns: ['Basin', 'Count Per Well', 'EF (scf whole gas/hour/unit)'],
      },
      {
        name: 'Gas Composition',
        columns: ['Region', 'Methane molar fraction'],
      },
      {
        name: 'Component AF',
        columns: [
          'Major equipment',
          'Valve',
          'Flange',
          'Connector',
          'OEL',
          'Pressure relief valve',
        ],
      },
      {
        name: 'Component EF',
        columns: ['Component', 'Leaker EF (scfh methane)'],
      },
      {
        name: 'AMI Q3 One Time Events',
        columns: [
          'Region',
          'AMI Operator',
          'Facility Name',
          'One Time Event Total (kg)',
        ],
      },
      {
        name: 'Root Cause Analysis',
        columns: [
          'Region',
          'Operator',
          'Equipment Type',
          'Equipment ID',
          'Emission Location ID',
          'Duration (Days)',
        ],
      },
    ],
    title: 'Measurement Informed Inventory: Upstream',
    fileField: 'file',
    isDownloadAllowed: true,
    fileTemplateToDownload: 'MIIModel_Input_template.xlsx',
    submitButtonLabel: 'Run MII Model',
    rowToValidate: 0,
    apiUrl: {
      DEV: 'https://flexplanfuncmodels.azurewebsites.net/api/upstream',
      QA: 'https://flexplanfuncmodelsqa.azurewebsites.net/api/upstream',
      PROD: 'https://flexplanfuncmodelsprod.azurewebsites.net/api/upstream',
    },
    paramsMapping: undefined,
  },
  meet_pneumatics: {
    allowedFileTypes: ['xls', 'xlsx'],
    sheets: [],
    title: 'Methane Emissions Estimation Tool (MEET): Pneumatic Controllers',
    fileField: 'file',
    isDownloadAllowed: false,
    description: 'Model 3 description',
    paramsMapping: undefined,
    rowToValidate: 1,
    apiUrl: {
      DEV: 'https://flexplanfuncmodels.azurewebsites.net/api/pneumatics', //dev env azure
      // DEV: 'http://localhost:3000/FlexTest',   //node
      // DEV: 'http://localhost:7071/api/pneumatics', // local pyt
      QA: 'https://flexplanfuncmodelsqa.azurewebsites.net/api/pneumatics',
      PROD: 'https://flexplanfuncmodelsprod.azurewebsites.net/api/pneumatics',
    },
    submitButtonLabel: 'Run MEET Model',
  },
  ami_dashboard: {
    allowedFileTypes: ['xls', 'xlsx'],
    sheets: [],
    title: 'AMI Dashboard',
    fileField: 'file',
    isDownloadAllowed: false,
    description: 'AMI Dashboard description',
    paramsMapping: undefined,
    rowToValidate: 1,
    apiUrl: {
      DEV: 'https://flexplanfuncmodels.azurewebsites.net/api/pneumatics', //dev env azure
      // DEV: 'http://localhost:3000/FlexTest',   //node
      // DEV: 'http://localhost:7071/api/pneumatics', // local pyt
      QA: 'https://flexplanfuncmodelsqa.azurewebsites.net/api/pneumatics',
      PROD: 'https://flexplanfuncmodelsprod.azurewebsites.net/api/pneumatics',
    },
    submitButtonLabel: 'Run MEET Model',
  },
};
